// src/hooks/useFocusScrollIntoView.ts
import isMobile from "is-mobile";
import { useEffect } from "react";

type Options = {
	bottomMargin?: number;
};

export function useFocusScrollIntoView({ bottomMargin = 100 }: Options = {}) {
	useEffect(() => {
		// SSR / non-browser guard
		if (typeof window === "undefined" || typeof document === "undefined") return;

		if (!isMobile()) return;
		if (!("visualViewport" in window)) return;

		const isEditable = (el: Element | null) => {
			if (!el) return false;
			const anyEl = el as any;
			if (anyEl.isContentEditable) return true;
			const tag = (anyEl.tagName || "").toLowerCase();
			if (tag === "textarea" || tag === "select") return true;
			if (tag === "input") {
				const type = (anyEl.type || "").toLowerCase();
				return type !== "hidden";
			}
			return false;
		};

		const onFocusIn = (e: FocusEvent) => {
			const el = e.target as HTMLElement | null;
			if (!el || !isEditable(el)) return;

			// Wait a tick for the keyboard/viewport to settle
			requestAnimationFrame(() => {
				setTimeout(() => {
					const vv = window.visualViewport!;
					const rect = el.getBoundingClientRect();

					const visibleTop = vv.offsetTop;
					const visibleBottom = vv.offsetTop + vv.height - bottomMargin;

					let dy = 0;
					if (rect.bottom > visibleBottom) {
						dy = rect.bottom - visibleBottom;
					} else if (rect.top < visibleTop) {
						dy = rect.top - visibleTop;
					}

					if (dy !== 0) {
						window.scrollTo({
							top: window.pageYOffset + dy,
							left: 0,
							behavior: "instant"
						});
					}
				}, 500);
			});
		};

		document.addEventListener("focusin", onFocusIn, true);
		return () => {
			document.removeEventListener("focusin", onFocusIn, true);
		};
	}, [bottomMargin]);
}
